import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { queryClient } from "@/lib/query-client";
import { removeCookie, setCookie } from "@/lib/utils/cookie-utils";
import type { components } from "@/types/api/api-types";
import { useSellerVerificationUpload } from "./seller-verification-upload";

type AuthUserDto = components["schemas"]["AuthUserDto"];

interface AuthState {
  isLoggedIn: boolean;
  isLoginDialogOpen: boolean;
  user: AuthUserDto | null;
  token: string | null;
  hasExplicitlyLoggedOut: boolean;
  isImpersonating: boolean;
  originalToken: string | null;
  originalUser: AuthUserDto | null;

  setIsLoggedIn: (isLoggedIn: boolean) => void;
  setUser: (user: AuthUserDto | null) => void;
  setToken: (token: string | null) => void;
  setIsLoginDialogOpen: (isOpen: boolean) => void;
  setHasExplicitlyLoggedOut: (value: boolean) => void;
  startImpersonation: (token: string, user: AuthUserDto) => void;
  endImpersonation: () => void;
  logout: (options?: LogoutOptions) => Promise<void>;
}

interface LogoutOptions {
  /**
   * The server rejected the token (401), the user did not ask to sign out.
   *
   * `hasExplicitlyLoggedOut` is what AutoLoginEffect reads as "this person just
   * signed out on purpose, do not re-prompt". Setting it on an expiry made the
   * session end silently: the wallet stays connected, so ConnectButton hides
   * itself and TopNav keeps rendering the signed-in cluster, while nothing ever
   * offers a way back in. Leave the flag alone so the sign-in prompt reopens.
   */
  sessionExpired?: boolean;
}

// Custom storage that syncs to both localStorage and cookies
const createAuthStorage = () => {
  // Only create localStorage storage on the client side
  const localStorageStorage =
    typeof window !== "undefined"
      ? createJSONStorage(() => window.localStorage)
      : null;

  return {
    getItem: (name: string) => {
      if (localStorageStorage) {
        return localStorageStorage.getItem(name);
      }
      // SSR fallback: return null (data will come from cookies on client hydration)
      return null;
    },
    setItem: (name: string, value: unknown) => {
      // Store in localStorage using the typed storage (client-side only)
      if (localStorageStorage) {
        localStorageStorage.setItem(
          name,
          value as Parameters<typeof localStorageStorage.setItem>[1],
        );
      }

      // Also store in cookies for SSR
      if (typeof document !== "undefined") {
        try {
          // Zustand persist stores the value as JSON string, so we stringify it for the cookie
          const cookieValue =
            typeof value === "string" ? value : JSON.stringify(value);
          // Store auth state in cookie (max 7 days, httpOnly will be false for client-side access)
          setCookie("auth", cookieValue, {
            maxAge: 7 * 24 * 60 * 60,
            path: "/",
            sameSite: "Lax",
          });
        } catch {
          // Ignore parse errors
        }
      }
    },
    removeItem: (name: string) => {
      if (localStorageStorage) {
        localStorageStorage.removeItem(name);
      }
      if (typeof document !== "undefined") {
        removeCookie("auth", "/");
      }
    },
  };
};

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      isLoggedIn: false,
      user: null,
      token: null,
      isLoginDialogOpen: false,
      hasExplicitlyLoggedOut: false,
      isImpersonating: false,
      originalToken: null,
      originalUser: null,

      setIsLoginDialogOpen: (isOpen) => set({ isLoginDialogOpen: isOpen }),
      setIsLoggedIn: (isLoggedIn) => set({ isLoggedIn: isLoggedIn }),
      setUser: (user) => set({ user: user }),
      setHasExplicitlyLoggedOut: (value) =>
        set({ hasExplicitlyLoggedOut: value }),
      setToken: (token) => {
        set({ token: token });
        // Also sync to cookie immediately for SSR
        if (typeof document !== "undefined") {
          try {
            const currentState = useAuth.getState();
            const authData = {
              state: {
                isLoggedIn: currentState.isLoggedIn,
                user: currentState.user,
                token: token,
                isLoginDialogOpen: currentState.isLoginDialogOpen,
                hasExplicitlyLoggedOut: currentState.hasExplicitlyLoggedOut,
              },
            };
            setCookie("auth", JSON.stringify(authData), {
              maxAge: 7 * 24 * 60 * 60,
              path: "/",
              sameSite: "Lax",
            });
          } catch {
            // Ignore errors
          }
        }
      },
      startImpersonation: (token, user) => {
        const currentState = get();
        set({
          originalToken: currentState.token,
          originalUser: currentState.user,
          token,
          user,
          isImpersonating: true,
          isLoggedIn: true,
        });
      },
      endImpersonation: () => {
        const currentState = get();
        if (!currentState.isImpersonating) return;
        set({
          token: currentState.originalToken,
          user: currentState.originalUser,
          isImpersonating: false,
          originalToken: null,
          originalUser: null,
        });
      },
      logout: async (options) => {
        set({
          isLoggedIn: false,
          user: null,
          token: null,
          hasExplicitlyLoggedOut: !options?.sessionExpired,
          isImpersonating: false,
          originalToken: null,
          originalUser: null,
        });
        removeCookie("auth", "/");
        // Reset other user-scoped stores
        useSellerVerificationUpload.getState().reset();
        // Every cached response belongs to the session that just ended, so drop
        // them here, synchronously, as part of ending it.
        //
        // WalletLayer's SessionCacheResetEffect also clears on the isLoggedIn
        // true→false transition and additionally covers `walletQueryClient`.
        // This is not a replacement for it: it closes the window between logout
        // resolving and that effect running, and it still fires on paths where
        // WalletLayer is not mounted. Clearing an already-clear cache is a
        // no-op, so the overlap is free.
        queryClient.clear();
      },
    }),
    {
      name: "auth",
      storage: createAuthStorage(),
      // Persist the session itself — nothing else. `hasExplicitlyLoggedOut` is
      // transient intent ("I just logged out, don't immediately re-prompt me"),
      // and `isLoginDialogOpen` is UI state. Persisting the former meant a
      // single logout suppressed the sign-in prompt on every later visit
      // (AutoLoginEffect is gated on `!hasExplicitlyLoggedOut`), so a connected
      // wallet silently stayed unauthenticated until a protected route forced
      // the issue.
      partialize: (state) => ({
        isLoggedIn: state.isLoggedIn,
        user: state.user,
        token: state.token,
        isImpersonating: state.isImpersonating,
        originalToken: state.originalToken,
        originalUser: state.originalUser,
      }),
    },
  ),
);

export const authState = useAuth.getState();
